import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { subMonths } from "date-fns";
import { useEffect, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useLocation } from "react-router";
import { createSearchParams, Link, useSearchParams } from "react-router-dom";

import { ReactComponent as LockIcon } from "assets/icons/lock.svg";
import Box from "components/Box";
import Chip from "components/Chip";
import DateRangeField from "components/DateRangeField";
import Empty from "components/Empty";
import Pagination from "components/Pagination";
import RadioButtonGroup from "components/RadioButtonGroup";
import EnhancedSearchWithTypeField from "components/SearchWithTypeField";
import Select from "components/Select";
import SortButton from "components/SortButton";
import Table from "components/Table";
import TitleHeading from "components/TitleHeading";
import { useGetCourses, useGetQuestions } from "hooks/apis/question";
import useHandleParams from "hooks/useHandleParams";
import format from "lib/format";
import { Question } from "types/types";
import {
  completedMonitoringStatusOptions,
  monitoringSearchTypeOptions,
  onGoingMonitoringStatusOptions,
  qnaSearchTypeOptions,
  questionTypeOptions,
  subjectTypeOptions,
} from "utils/const";

type FormValues = {
  page: number;
  startDate: string;
  endDate: string;
  course: string;
  subjectType: string;
  questionType: string;
  questionStatus: string;
  searchType: string;
  searchText: string;
  sort?: string;
};

const STATUS: Record<string, string> = {
  "/monitoring/work/ongoing": "대기중",
  "/monitoring/work/completed": "완료",
};

const SECTION_TITLES: Record<string, string> = {
  "/monitoring/work/completed": "검수 완료",
};

const MonitoringWorkPage = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const { methods, searchRef } = useHandleParams<FormValues>({
    defaultValues: {
      startDate: format(subMonths(new Date(), 1), "yyyy-MM-dd"),
      endDate: format(new Date(), "yyyy-MM-dd"),
      course: "all",
      subjectType: "all",
      questionType: "all",
      questionStatus: "all",
      searchType: "title",
      searchText: "",
      page: 1,
      sort:
        pathname === "/monitoring/work/completed"
          ? "monitoredDesc"
          : "answeredDesc",
    },
  });

  const form = methods.watch();

  const { data, totalItems, currentPage, isFetching } = useGetQuestions({
    status: STATUS[pathname],
    size: 20,
    ...form,
    monitoring: 1,
  });
  const { data: courses } = useGetCourses({
    startDate: form.startDate,
    endDate: form.endDate,
  });
  const coursesOptions = useMemo(
    () => [{ label: "강좌 전체", value: "all" }, ...(courses || [])],
    [courses],
  );

  useEffect(() => {
    if (pathname === "/monitoring/work/ongoing") {
      queryClient.invalidateQueries({ queryKey: ["monitoringCount"] });
    }
  }, [pathname]);

  useEffect(() => {
    if (courses?.length) {
      methods.setValue("course", methods.getValues("course"));
    }
  }, [courses]);

  const handleQuestionClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    question: Question,
  ) => {
    if (question.answers?.[0]?.monitoringStatus === 1) {
      e.preventDefault();
    }
  };

  return (
    <FormProvider {...methods}>
      <main className="p-[1.875rem] pl-[15rem] 1350:pl-[16rem] 1400:pl-[16.875rem] w-full">
        <TitleHeading
          className="mb-[0.625rem]"
          title="조교 작업 모니터링"
          description="조교 작업 현황 모니터링 화면입니다."
        />
        <Box className="flex justify-between">
          <h3 className="text-18 font-medium">
            {SECTION_TITLES[pathname] ??
              `대기중 (총 ${(totalItems ?? 0).toLocaleString()}건)`}
          </h3>
        </Box>
        <Box
          backgroundColor="gray"
          className="py-[0.625rem] px-[1.875rem] flex gap-[3.125rem] mb-[0.625rem] 1500:!pr-[6.5rem]"
        >
          <div className="flex-1">
            <dl className="grid grid-cols-[7.5rem_1fr] 1600:grid-cols-[8.125rem_1fr] gap-y-[0.625rem]">
              <dt className="flex items-center">기간 검색</dt>
              <dd>
                <DateRangeField />
              </dd>
              <dt className="flex items-center">강좌</dt>
              <dd>
                <Select name="course" options={coursesOptions} />
              </dd>
              <dt className="flex items-center">과목 유형</dt>
              <dd>
                <Select name="subjectType" options={subjectTypeOptions} />
              </dd>
            </dl>
          </div>
          <div className="flex-1">
            <dl className="grid grid-cols-[7.5rem_1fr] 1600:grid-cols-[8.125rem_1fr] gap-y-[0.625rem]">
              <dt className="flex items-center">질문 유형</dt>
              <dd>
                <Select name="questionType" options={questionTypeOptions} />
              </dd>
              <dt className="flex items-center">검수 상태</dt>
              <dd className="flex gap-[0.313rem]">
                <RadioButtonGroup
                  name="questionStatus"
                  options={
                    pathname.includes("ongoing")
                      ? onGoingMonitoringStatusOptions
                      : completedMonitoringStatusOptions
                  }
                  defaultValue="all"
                />
              </dd>
              <dt className="flex items-center">검색 상세</dt>
              <dd className="flex gap-[0.313rem]">
                <EnhancedSearchWithTypeField
                  ref={searchRef}
                  options={
                    pathname.includes("ongoing")
                      ? qnaSearchTypeOptions
                      : monitoringSearchTypeOptions
                  }
                />
              </dd>
            </dl>
          </div>
        </Box>
        <Box className="!py-5 min-h-[calc(100%-18.75rem)] flex flex-col">
          <span className="text-16 font-medium text-secondary">
            총 {(totalItems ?? 0).toLocaleString()}개
          </span>
          <Table
            classNames={[
              "max-w-[7.5rem] min-w-[5.5rem] w-[7.98%]",
              "max-w-[7.5rem] min-w-[5rem] w-[7.98%]",
              "max-w-[7.5rem] min-w-[7.75rem] w-[7.98%]",
              "flex-1 px-4 1500:px-6 1600:px-8 1700:px-10 2000:px-[3.75rem] text-left",
              "max-w-[6.25rem] min-w-[5.25rem] w-[6.65%]",
              "max-w-[6.25rem] min-w-[4rem] w-[6.65%]",
              "max-w-[8rem] min-w-[7.5rem] w-[6.25%]",
              "max-w-[9.5rem] min-w-[7rem] w-[10.11%]",
              "max-w-[8rem] min-w-[7.5rem] w-[6.25%]",
              "max-w-[9.5rem] min-w-[7rem] w-[10.11%]",
            ]}
          >
            <thead>
              <tr>
                <th>번호</th>
                <th>검수 상태</th>
                <th>과목유형</th>
                <th>제목</th>
                <th>질문 유형</th>
                <th>활성화</th>
                {pathname === "/monitoring/work/ongoing" ? (
                  <>
                    <th>작성자</th>
                    <th>
                      <SortButton name="created" defaultValue="created">
                        작성일
                      </SortButton>
                    </th>
                    <th>답변자</th>
                    <th>
                      <SortButton name="answered" defaultValue="created">
                        답변일
                      </SortButton>
                    </th>
                  </>
                ) : (
                  <>
                    <th>답변자</th>
                    <th>
                      <SortButton name="answered" defaultValue="created">
                        답변일
                      </SortButton>
                    </th>
                    <th>검수자</th>
                    <th>
                      <SortButton name="monitored" defaultValue="created">
                        검수일
                      </SortButton>
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {data?.map((question, index) => (
                <tr
                  key={question.bNo}
                  className={clsx({
                    "bg-lightGray cursor-not-allowed":
                      question.answers?.[0]?.monitoringStatus === 1,
                  })}
                >
                  <td>{totalItems - (index + 20 * (currentPage - 1))}</td>
                  <td>
                    <Chip
                      color={
                        question.answers?.[0]?.monitoringStatus === 2
                          ? question.status === "답변 완료"
                            ? "blue"
                            : "orange"
                          : question.answers?.[0]?.monitoringStatus === 0
                            ? "red"
                            : "gray"
                      }
                    >
                      {question.answers?.[0]?.monitoringStatus === 2
                        ? question.status === "답변 완료"
                          ? "완료"
                          : "재답변"
                        : question.answers?.[0]?.monitoringStatus === 0
                          ? "대기중"
                          : "검수중"}
                    </Chip>
                  </td>
                  <td>{question.subjectType}</td>
                  <td>
                    <Link
                      onClick={(e) => handleQuestionClick(e, question)}
                      className={clsx(
                        "flex items-center duration-100",
                        question.answers?.[0]?.monitoringStatus === 1
                          ? "cursor-not-allowed"
                          : "hover:underline",
                      )}
                      to={{
                        pathname:
                          question.answers?.[0]?.monitoringStatus === 1
                            ? ""
                            : `/monitoring/work/${question.bNo}`,
                        search: `?${createSearchParams(
                          JSON.parse(
                            JSON.stringify({
                              status: STATUS[pathname],
                              size: 20,
                              ...Object.fromEntries(searchParams.entries()),
                              monitoring: 1,
                            }),
                          ),
                        )}`,
                      }}
                      // state={{
                      //   status: STATUS[pathname],
                      //   size: 20,
                      //   ...Object.fromEntries(searchParams.entries()),
                      //   monitoring: 1,
                      // }}
                    >
                      {question.secret && (
                        <LockIcon className="size-6 shrink-0" />
                      )}
                      <p className="text-left line-clamp-1 break-all">
                        {question.title}
                      </p>
                    </Link>
                  </td>
                  <td>{question.questionType}</td>
                  <td>{question.enabled ? "Y" : "N"}</td>
                  {pathname === "/monitoring/work/ongoing" ? (
                    <>
                      <td>
                        <div className="flex flex-col items-center">
                          <span>{question.student?.studentName}</span>
                          <span className="text-12">
                            {question.student?.studentId}
                          </span>
                        </div>
                      </td>
                      <td>{format(question.registrationDate, "yyyy/MM/dd")}</td>
                      <td>
                        {question.answers?.length && (
                          <div className="flex flex-col items-center">
                            <span>
                              {question.answers?.[0]?.user?.name ??
                                question.answers?.[0]?.responderName}{" "}
                            </span>
                            <span className="text-12">
                              {question.answers?.[0]?.user?.userId ||
                                question.answers?.[0]?.responderId}
                            </span>
                          </div>
                        )}
                      </td>
                      <td>
                        {format(
                          question.answers?.[0]?.registrationDate,
                          "yyyy/MM/dd",
                        )}
                      </td>
                    </>
                  ) : (
                    <>
                      <td>
                        {question.answers?.length && (
                          <div className="flex flex-col items-center">
                            <span>
                              {question.answers?.[0]?.user?.name ??
                                question.answers?.[0]?.responderName}{" "}
                            </span>
                            <span className="text-12">
                              {question.answers?.[0]?.user?.userId ||
                                question.answers?.[0]?.responderId}
                            </span>
                          </div>
                        )}
                      </td>
                      <td>
                        {format(
                          question.answers?.[0]?.registrationDate,
                          "yyyy/MM/dd",
                        )}
                      </td>
                      <td>
                        <div className="flex flex-col items-center">
                          <span>{question.answers?.[0]?.monitor?.name}</span>
                          <span className="text-12">
                            {question.answers?.[0]?.monitor?.userId}
                          </span>
                        </div>
                      </td>
                      <td>
                        {format(
                          question.answers?.[0]?.lastStatusUpdate,
                          "yyyy/MM/dd",
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
          <Empty show={!isFetching && data && !data.length} />
          <Pagination total={totalItems} />
        </Box>
      </main>
    </FormProvider>
  );
};

export default MonitoringWorkPage;
